import { Component, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { RolesService, SettingsService, UpsertRoleRequest } from '@src/app/shared/backend/clients';
import { BaseListComponent } from '@src/app/shared/components/base-list/base-list.component';
import { SessionService } from '@src/app/shared/services/session.service';
import { PermissionService } from '@src/app/shared/services/permission.service';
import { Actions } from '@src/app/shared/backend/auth/Action';
import { TableSettingService } from '@src/app/shared/services/table.setting.service';
import { LandingPageService } from '@src/app/shared/services/landingPage.service';
import { firstValueFrom } from 'rxjs';
import { LandingPage } from '@src/app/shared/backend/clients/model/landingPage';

@Component({
    selector: 'app-roles-settings',
    templateUrl: './roles-settings.component.html',
    styleUrls: ['./roles-settings.component.scss'],
    host: {
        class: 'app-roles-settings'
    },
    standalone: false
})
export class RolesSettingsComponent extends BaseListComponent implements OnInit {

  features: any;
  actions: any;
  editRoleName: any = '';
  currentRole: any = { isActive: true };
  selectionOptions: any[] = [];
  defaultValue = 0;
  name = '';
  priority = 0;
  landingPage : LandingPage;
  landingPageOptions = this.landingPageService.getLandingPageOptions();

  showCreateModal = false;
  showDeleteModal = false;
  canManageSettings: boolean = false;

  constructor(public override tableSettingService: TableSettingService,
    public translate: TranslateService,
    public settingsService: SettingsService,
    public sessionService: SessionService,
    private permissionService: PermissionService,
    public rolesService: RolesService,
    private landingPageService: LandingPageService) {
    super();
  }
  initForm() { }

  async ngOnInit() {
    this.selectionOptions = [

      { label: this.translate.instant('ENUMS.DISABLED'), value: -1 },
      { label: this.translate.instant('ENUMS.NONE'), value: 0 },
      { label: this.translate.instant('ENUMS.USER'), value: 1 },
      { label: this.translate.instant('ENUMS.RETAILER'), value: 2 },
      { label: this.translate.instant('ENUMS.MARKET'), value: 3 },
    ];

    this.canManageSettings = await this.permissionService.hasActionPermissions(Actions.manage_systemsettings);
    await this.tableSettingService.init();
    await this.get();
  }

  async openCreateDefaultRoles(): Promise<void> {
    try {
      await firstValueFrom(this.rolesService.apiRolesCreateDefaultPost());
      await this.get();
    } catch (error) {
      console.error(error);
    }
  }

  openCreateModal(): void {
    this.resetData();
    this.currentRole = { isActive: true };
    this.showCreateModal = true;
  }

  private resetData() {
    this.name = '';
    this.priority = 0;
    this.landingPage = LandingPage.Profile;
    this.actions?.forEach((action: any) => {
      action.value = -1;
    });
  }

  onCancelCreate(): void {
    this.resetData();
    this.showCreateModal = false;
  }

  async onDisable(evt: any, rowData: any): Promise<void> {
    try {
      await firstValueFrom(
        this.rolesService.apiRolesIdDeactivatePut(rowData.id)
      );

      await this.get();
    } catch (error) {
      console.error(error);
    }
  }

  async onEnable(evt: any, rowData: any): Promise<void> {
    try {
      await firstValueFrom(
        this.rolesService.apiRolesIdActivatePut(rowData.id)
      );

      await this.get();
    } catch (error) {
      console.error(error);
    }
  }

  async onDelete(): Promise<void> {
    try {
      await firstValueFrom(
        this.rolesService.apiRolesDeleteIdDelete(this.currentRole.id)
      );

      this.showDeleteModal = false;
      await this.get();
    } catch (error) {
      console.error(error);
    }
  }

  onCancelDelete(): void {
    this.showDeleteModal = false;
  }

  override async get(){
    await super.setCache(TableSettingService.RolesTableName);
    this.filter.pageNumber = this.pageNumber;
    this.filter.pageSize = this.pageSize;

    this.page = await firstValueFrom(this.rolesService.apiRolesFilterTablePost(this.filter));
    this.data = this.page.results;

    this.actions = (await firstValueFrom(this.settingsService.apiSettingsActionsGet()))?.sort((a, b) => a?.name?.localeCompare(b.name ?? '') ?? 0);

    // use cached settings
    const settings = await this.sessionService.getCompanySettings();
    this.features = settings?.features?.sort((a, b) => a?.name?.localeCompare(b.name ?? '') ?? 0);
  }

  get paginatorFirst(): number {
    return this.pageNumber * this.pageSize;
  }

  async onPaginatorChange(event: { first: number; rows: number; page: number }): Promise<void> {
    this.pageNumber = event.page ?? 1;
    this.pageSize = event.rows ?? this.pageSize;

    await this.get();
  }

  getRoles(feature: any) {
    return this.actions?.filter((x: any) => x.featureId == feature.id);
  }

  goToDelete(event: any, data: any) {
    this.currentRole = data;
    this.editRoleName = data?.name;
    this.showDeleteModal = true;
  }

  async goToDetails(event: any, id: string) {
    this.currentRole = await firstValueFrom(this.rolesService.apiRolesDetailsIdGet(id)) ?? { isActive: true };
    this.resetData();
    this.name = this.currentRole?.name;
    this.priority = this.currentRole?.priority;
    this.landingPage = this.currentRole?.landingPage;

    this.currentRole.features?.forEach((f: any) => {
      //console.log(f);
      f.actions?.forEach((d: any) => {
        //console.log(d);
        var action = this.actions.find((x: any) => x.id == d.id);
        if (action) {
          //console.log(action);
          action.value = d.isActive == false ? -1 : d.restrictionType;
        }
      });
    });

    //console.log(this.currentRole, this.currentRole.roleActions);
    this.showCreateModal = true;
  }

  changeAction(data: any){
    if (data.value == 2) {
      this.resetAction(3, 2);
    }
    if (data.value == 3) {
      this.resetAction(2, 3);
    }
  }

  resetAction(checkValue: any, newValue: any){
    this.actions.forEach((a: any) => {
      if (a.value == checkValue){
        a.value = newValue;
      }
    });
  }

  async save(): Promise<void> {
    this.sessionService.setLoading(true);

    try {
      const roleActions = this.actions
        .filter((action: any) => action.value !== -1)
        .map((action: any) => ({
          actionId: action.id,
          restrictionType: action.value,
          isActive: true
        }));

      const data: UpsertRoleRequest = {
        name: this.currentRole.name,
        priority: this.currentRole.priority,
        landingPage: this.currentRole.landingPage.toString(),
        roleActions,
        roleId: this.currentRole.id
      };

      await firstValueFrom(this.rolesService.apiRolesUpsertPut(data));

      this.showCreateModal = false;
      await this.get();
    } catch (error) {
      console.error(error);
    } finally {
      this.sessionService.setLoading(false);
    }
  }
}
